import { MACROS } from '@src/graphql/queries/macro';
import { usePaginatedFetchItems } from 'pe-core-frontend';
/**
 * Custom hook to fetch and manage paginated macros.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.id - The ID of macro.
 * @param {string} params.clientId - The client ID to filter macros.
 * @param {string} params.searchText - The search text to filter macros.
 * @param {number} [params.initialPageSize=10] - The initial number of items per page.
 *
 * @returns {Object} The state and functions to manage paginated macros.
 * @returns {Array<Macro>} macros - The list of macros.
 * @returns {boolean} loading - The loading state.
 * @returns {number} page - The current page number.
 * @returns {number} pageSize - The current number of items per page.
 * @returns {Function} setPage - Function to set the current page number.
 * @returns {Function} setPageSize - Function to set the number of items per page.
 * @returns {Error} error - The error object if an error occurred.
 * @returns {number} total - The total number of items.
 * @returns {Function} find - Function to find items.
 * @returns {string} orderBy - The field to order by.
 * @returns {string} orderByDir - The direction to order by (asc/desc).
 * @returns {Function} setOrderBy - Function to set the field to order by.
 * @returns {Function} setOrderByDir - Function to set the direction to order by.
 * @returns {Function} refetch - Function to refetch the items.
 */
export const useMacros = ({ id, clientId, searchText, initialPageSize = 10, skip, }) => {
    const { page, setPage, pageSize, setPageSize, total, items: macros, loading, error, find, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = usePaginatedFetchItems({
        spec: { id, clientId, searchText },
        fetchQuery: MACROS,
        dataProp: 'macros',
        initialPageSize,
        skip,
    });
    return {
        macros,
        loading,
        page,
        pageSize,
        setPage,
        setPageSize,
        error,
        total,
        find,
        orderBy,
        orderByDir,
        setOrderBy,
        setOrderByDir,
        refetch,
    };
};
